import { memo, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { isEqual } from '@Sage/ssm-utils';
import {
    CopilotBar as CopilotBarComponent,
    CopilotContext,
    COPILOT_BAR_LOADING_STATES,
} from '@Sage/ssm-web5';
import { useDispatch, useSelector } from 'react-redux';

import useCopilot from 'hooks/useCopilot';
import { mergeCopilotBarOpenWithUserPreference } from 'utils/copilotPanels';
import { userflowHideAndShow } from 'utils/userflow';

import './styles.scss';
import useCopilotBarScenarioMatch from './useCopilotBarScenarioMatch';

const COPILOT_BAR_ON_CLASS = 'copilot-bar--on';
const COPILOT_BAR_LOADING_CLASS = 'copilot-bar--loading';
const COPILOT_BAR_MINIMIZED_CLASS = 'copilot-bar--minimized';

/** Clears bar fields not set by scenario buildConfig; avoid stale merge when chatSetup changes. */
const COPILOT_BAR_EPHEMERAL_RESET = {
    doneMessage: null,
    feedback: null,
    files: [],
    inputMode: null,
    inputPlaceholder: null,
    loadingPhrases: [],
    uploadingFiles: false,
};

/** Align with @Sage/ssm-web5 useCopilot initial copilotBarProps so merge clears all session UI state. */
const INITIAL_COPILOT_BAR_STATE = {
    chatSetup: null,
    doneMessage: null,
    feedback: null,
    files: [],
    height: 0,
    ignoreCopilotSidebarInset: false,
    initialLoadingPhrase: null,
    inputMode: null,
    inputPlaceholder: null,
    isMinimized: false,
    isOpen: false,
    loadingPhrases: [],
    loadingState: COPILOT_BAR_LOADING_STATES.IDLE,
    padding: 24,
    pinnedToRight: false,
    showInputModeControls: true,
    transition: 'fade',
    uploadingFiles: false,
    width: '100%',
};

const CopilotBar = () => {
    // ========================================
    // HOOKS
    // ========================================
    const dispatch = useDispatch();
    const { appState, matchingScenario } = useCopilotBarScenarioMatch();
    const copilotContext = useContext(CopilotContext);
    const { copilotBarProps, setCopilotBarProps } = copilotContext;
    const { isMinimized, isOpen, loadingState } = copilotBarProps;

    // ========================================
    // LOCAL STATE & REFS
    // ========================================
    const prevStateRef = useRef(INITIAL_COPILOT_BAR_STATE);
    const entityCrudStateRef = useRef({ activeCrudName: null, entityCrud: {} });
    const pendingScenarioConfigRef = useRef(null);
    const hasShownDisclaimerRef = useRef(false);

    entityCrudStateRef.current = {
        activeCrudName: appState.entityCrudState.activeCrudName,
        entityCrud: appState.entityCrudState.entityCrud,
    };

    const getEntityCrudState = () => entityCrudStateRef.current;

    const copilotActivatedDisclaimer = useSelector(
        (state) => state.config.permission?.danaActivatedDisclaimer || false,
    );

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================

    const onConfirmDisclaimerSuccess = useCallback(() => {
        if (pendingScenarioConfigRef.current && setCopilotBarProps) {
            const merged = mergeCopilotBarOpenWithUserPreference(pendingScenarioConfigRef.current);
            prevStateRef.current = merged;
            setCopilotBarProps({ ...COPILOT_BAR_EPHEMERAL_RESET, ...merged });
            pendingScenarioConfigRef.current = null;
        }
    }, [setCopilotBarProps]);

    const { copilotDisclaimerOnClose, renderCopilotDisclaimer, setCopilotModalAfterDisclaimer } =
        useCopilot({
            onConfirmDisclaimerSuccess,
            overrideModalOverlayClassName: 'fm-copilot-bar__disclaimer-overlay',
        });

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================

    const copilotBarState = useMemo(() => {
        if (!matchingScenario) return INITIAL_COPILOT_BAR_STATE;

        if (!copilotActivatedDisclaimer) return INITIAL_COPILOT_BAR_STATE;

        const config = matchingScenario.buildConfig({
            dispatch,
            getEntityCrudState,
            setCopilotBarProps,
            state: appState,
        });

        return config || INITIAL_COPILOT_BAR_STATE;
    }, [appState, copilotActivatedDisclaimer, dispatch, matchingScenario, setCopilotBarProps]);

    // ========================================
    // EFFECTS
    // ========================================

    useEffect(() => {
        if (matchingScenario && !copilotActivatedDisclaimer && !hasShownDisclaimerRef.current) {
            const config = matchingScenario.buildConfig({
                dispatch,
                getEntityCrudState,
                setCopilotBarProps,
                state: appState,
            });
            pendingScenarioConfigRef.current = config;
            hasShownDisclaimerRef.current = true;

            setCopilotModalAfterDisclaimer(true);
        }

        if (!matchingScenario) {
            hasShownDisclaimerRef.current = false;
            pendingScenarioConfigRef.current = null;
        }
    }, [
        appState,
        copilotActivatedDisclaimer,
        dispatch,
        matchingScenario,
        setCopilotBarProps,
        setCopilotModalAfterDisclaimer,
    ]);

    useEffect(() => {
        if (!matchingScenario && setCopilotBarProps) {
            prevStateRef.current = INITIAL_COPILOT_BAR_STATE;
            setCopilotBarProps(INITIAL_COPILOT_BAR_STATE);
        }
    }, [matchingScenario, setCopilotBarProps]);

    useEffect(() => {
        if (!setCopilotBarProps) return;

        const newProps = mergeCopilotBarOpenWithUserPreference(copilotBarState);

        const { chatSetup: prevChatSetup, ...prevComparable } = prevStateRef.current;
        const { chatSetup: newChatSetup, ...newComparable } = newProps;

        const { cb: prevCb, ...prevChatSetupComparable } = prevChatSetup || {};
        const { cb: newCb, ...newChatSetupComparable } = newChatSetup || {};

        const chatSetupChanged = !isEqual(prevChatSetupComparable, newChatSetupComparable);
        const propsChanged = !isEqual(prevComparable, newComparable);

        if (chatSetupChanged || propsChanged) {
            prevStateRef.current = newProps;
            const payload = chatSetupChanged
                ? { ...COPILOT_BAR_EPHEMERAL_RESET, ...newProps }
                : newProps;
            setCopilotBarProps(payload);
        }
    }, [copilotBarState, setCopilotBarProps]);

    useEffect(() => {
        if (document.body) {
            const { classList } = document.body;

            const shouldHaveOnClass = isOpen;
            const shouldHaveLoadingClass =
                isOpen && loadingState === COPILOT_BAR_LOADING_STATES.LOADING;
            const shouldHaveMinimizedClass = isOpen && isMinimized;

            classList.toggle(COPILOT_BAR_ON_CLASS, shouldHaveOnClass);
            classList.toggle(COPILOT_BAR_LOADING_CLASS, shouldHaveLoadingClass);
            classList.toggle(COPILOT_BAR_MINIMIZED_CLASS, shouldHaveMinimizedClass);
        }

        return () => {
            document.body?.classList.remove(
                COPILOT_BAR_ON_CLASS,
                COPILOT_BAR_LOADING_CLASS,
                COPILOT_BAR_MINIMIZED_CLASS,
            );
        };
    }, [isMinimized, isOpen, loadingState]);

    useEffect(() => {
        if (isOpen) {
            userflowHideAndShow('hide');
        } else {
            userflowHideAndShow('show');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            copilotDisclaimerOnClose();
        }
    }, [copilotDisclaimerOnClose, isOpen]);

    // ========================================
    // RENDER
    // ========================================
    return (
        <>
            <CopilotBarComponent className="ssm-copilot-bar" />
            {renderCopilotDisclaimer}
        </>
    );
};

export default memo(CopilotBar);
